import React, { useEffect, useRef, useState } from "react";
import GridContainer from "@components/Grid/GridContainer.js";
import GridItem from "@components/Grid/GridItem.js";
import OvsTable from "@components/ReactTable/OvsTable";
import { Column } from "react-table";
import { itemProcessData } from "../constants/itemBatchTableData";
import CardWrapper from "@components/Card/CardWrapper";
import {
  Box,
  Chip,
  LinearProgress,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Button from "@components/CustomButtons/Button";
import { useLazyGetBatchExportProgressQuery } from "../queries";
import useNotifier from "hooks/useNotifier";

const POLL_INTERVAL_MS = 4000;

const useStyles = makeStyles((theme) => ({
  exportSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  stateChip: {
    marginLeft: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    marginTop: theme.spacing(0.5),
  },
}));

interface IBatchExportProgress {
  state?: string;
  processedItems?: number;
  totalItems?: number;
  updatedOn?: string;
}

interface IProps {
  /** Static actionProgress snapshot from the table row (used as initial data). */
  data: any;
  /** Batch ID — enables live polling when provided. */
  batchId?: string;
  /** Pre-signed S3 URL returned by the v2 export query. When set, the export
   *  progress section is shown and polling drives the UI. */
  exportDownloadUrl?: string;
  /** Called when the user clicks the Download file button (not auto-fired). */
  onDownloadTriggered?: () => void;
  /** Called when the export reaches the Failed state so the parent can clean
   *  up sessionStorage and allow a retry. */
  onExportFailed?: () => void;
  /** Called when the user explicitly cancels an in-progress or stuck export so
   *  the parent can clear sessionStorage and reset state for a fresh attempt. */
  onCancelExport?: () => void;
}

const stateChipColor = (state?: string): "default" | "primary" | "secondary" => {
  if (state === "Completed") return "primary";
  if (state === "Failed") return "secondary";
  return "default";
};

const STALE_EXPORT_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

const BatchProgressPage: React.FC<IProps> = ({
  data,
  batchId,
  exportDownloadUrl,
  onDownloadTriggered,
  onExportFailed,
  onCancelExport,
}) => {
  const classes = useStyles();
  const notify = useNotifier();

  const [liveActionProgress, setLiveActionProgress] = useState<any>(null);
  const [transformedData, setTransformedData] = useState<any[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadRetryAttempt, setDownloadRetryAttempt] = useState(0);

  // How many times to retry a 403/404 before giving up. A 403 with the
  // s3:ListBucket message (or a 404 NoSuchKey) typically means the backend
  // marked the job Completed a moment before S3 finalised the object — a
  // transient race condition that self-heals within a few seconds.
  const DOWNLOAD_MAX_RETRIES = 3;
  const DOWNLOAD_RETRY_DELAY_MS = 2500;
  const FETCH_TIMEOUT_MS = 5000;

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    onDownloadTriggered?.();
  };

  const handleDownload = async () => {
    if (!exportDownloadUrl) return;
    setDownloading(true);
    setDownloadRetryAttempt(0);

    try {
      for (let attempt = 0; attempt <= DOWNLOAD_MAX_RETRIES; attempt++) {
        if (attempt > 0) setDownloadRetryAttempt(attempt);

        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

        try {
          const response = await fetch(exportDownloadUrl, { signal: controller.signal });
          clearTimeout(abortTimer);

          if (!response.ok) {
            // 403 AccessDenied or 404 NoSuchKey: the S3 object may not have
            // been written yet even though the backend reported Completed.
            // Retry with a short back-off before surfacing the error so the
            // transient race condition can self-heal.
            if ((response.status === 403 || response.status === 404) && attempt < DOWNLOAD_MAX_RETRIES) {
              await new Promise<void>((res) => setTimeout(res, DOWNLOAD_RETRY_DELAY_MS));
              continue;
            }
            notify({
              status: "error",
              text: `Export file unavailable (HTTP ${response.status}). The file may still be processing or the link has expired — please retry the export.`,
            });
            return;
          }

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objectUrl;
          // Derive filename from the S3 path, strip query string params.
          const pathname = new URL(exportDownloadUrl).pathname;
          a.download = pathname.split("/").pop() || "factory-export.csv";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
          onDownloadTriggered?.();
          return;
        } catch (err) {
          clearTimeout(abortTimer);
          if (err instanceof Error && err.message.startsWith("HTTP ")) {
            // Safety net — the !response.ok path above is the primary handler.
            notify({
              status: "error",
              text: "Export file unavailable. Please retry the export.",
            });
            return;
          }
          // Network-level failure: CORS blocked the fetch (TypeError) or the
          // request timed out (AbortError). Fall back to a browser navigation —
          // this is not an XHR so CORS does not apply. We cannot inspect the
          // HTTP status through an opaque CORS failure, so there is no point
          // delaying here (it would slow every download without helping).
          openInNewTab(exportDownloadUrl);
          return;
        }
      }

      // All retries exhausted.
      notify({
        status: "error",
        text: "Export file unavailable after multiple attempts. The file may still be processing or the link has expired — please retry the export.",
      });
    } finally {
      setDownloading(false);
      setDownloadRetryAttempt(0);
    }
  };

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Prevents the failure notification from showing more than once on remount. */
  const failureNotifiedRef = useRef(false);
  /** Fires once if the export stays non-terminal for longer than STALE_EXPORT_TIMEOUT_MS. */
  const staleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isStale, setIsStale] = useState(false);

  const handleCancelExport = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    setIsStale(false);
    onCancelExport?.();
  };

  const [fetchProgress, { data: progressData, error: progressError }] =
    useLazyGetBatchExportProgressQuery();

  // Start polling when the drawer mounts with a batchId; stop on unmount.
  useEffect(() => {
    if (!batchId) return;

    const poll = () => fetchProgress({ variables: { id: batchId } });
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start a stale-export watchdog when an export URL is present.
  // Cancelled/cleared if the export completes or fails before the timeout.
  useEffect(() => {
    if (!exportDownloadUrl) return;
    if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    setIsStale(false);

    staleTimerRef.current = setTimeout(() => {
      setIsStale(true);
    }, STALE_EXPORT_TIMEOUT_MS);

    return () => {
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
    };
  }, [exportDownloadUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync polled result into local state.
  useEffect(() => {
    const ap = progressData?.getSpecificItemBatch?.actionProgress;
    if (ap) setLiveActionProgress(ap);
  }, [progressData]);

  // Stop polling and surface the error if the progress query itself fails.
  useEffect(() => {
    if (!progressError) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    notify({ status: "error", text: "Could not refresh progress. Please close and reopen." });
  }, [progressError]); // eslint-disable-line

  // Live data takes precedence over the static snapshot for the summary table.
  const activeProgress = liveActionProgress ?? data;

  // Per spec: batchExport must ONLY come from live polled data. The DB snapshot
  // in `data` may hold a stale Completed/Failed/InProgress state from a previous
  // run and must never drive the export progress UI.
  const batchExport: IBatchExportProgress | undefined = liveActionProgress?.batchExport;

  // Handle terminal export states — stop polling only, no auto-download.
  useEffect(() => {
    if (!batchExport?.state) return;

    if (batchExport.state === "Completed") {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      setIsStale(false);
    }

    if (batchExport.state === "Failed" && !failureNotifiedRef.current) {
      failureNotifiedRef.current = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (staleTimerRef.current) clearTimeout(staleTimerRef.current);
      setIsStale(false);
      onExportFailed?.();
      notify({ status: "error", text: "Export failed. Please try again." });
    }
  }, [batchExport?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transform the full actionProgress map for the summary table.
  useEffect(() => {
    if (!activeProgress) return;
    const rows = Object.entries(activeProgress).map(([actionName, details]) => ({
      actionName,
      state: (details as any).state,
      pendingItems:
        (details as any).totalItems != null
          ? (details as any).totalItems - ((details as any).processedItems || 0)
          : undefined,
      updatedOn: (details as any).updatedOn,
    }));
    setTransformedData(rows);
  }, [activeProgress]);

  const pct =
    batchExport?.totalItems
      ? Math.round(((batchExport.processedItems || 0) / batchExport.totalItems) * 100)
      : batchExport?.state === "Completed"
      ? 100
      : 0;

  // Show indeterminate bar while:
  //  • batchExport hasn't appeared in actionProgress yet (backend queued, not started)
  //  • batchExport is InProgress but totalItems isn't known yet (< 3 000 item batches)
  const isIndeterminate =
    !batchExport ||
    (batchExport.state === "InProgress" && !batchExport.totalItems);

  const exportStatusText = () => {
    if (!batchExport) return "Export queued — waiting for backend…";
    switch (batchExport.state) {
      case "InProgress":
        return "Preparing download…";
      case "Completed":
        return "File ready — click Download file to save.";
      case "Failed":
        return "Export failed. Please try again.";
      default:
        return "Starting export…";
    }
  };

  return (
    <GridContainer>
      {/* Export progress section — only visible during an active download session */}
      {exportDownloadUrl && (
        <GridItem xs={12}>
          <div className={classes.exportSection}>
            <Box display="flex" alignItems="center">
              <Typography className={classes.title}>Export Progress</Typography>
              <Chip
                label={batchExport?.state ?? "Queued"}
                size="small"
                color={stateChipColor(batchExport?.state)}
                className={classes.stateChip}
              />
            </Box>

            <LinearProgress
              className={classes.progressBar}
              variant={isIndeterminate ? "indeterminate" : "determinate"}
              value={isIndeterminate ? undefined : pct}
              color={batchExport?.state === "Failed" ? "secondary" : "primary"}
            />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography className={classes.meta}>
                {isStale
                  ? "Export appears stuck. You can cancel and retry."
                  : exportStatusText()}
              </Typography>
              {batchExport?.totalItems ? (
                <Typography className={classes.meta}>
                  {(batchExport.processedItems || 0).toLocaleString()} /{" "}
                  {batchExport.totalItems.toLocaleString()} items ({pct}%)
                </Typography>
              ) : null}
            </Box>

            <Box mt={1} display="flex" style={{ gap: 8 }}>
              {batchExport?.state === "Completed" && exportDownloadUrl && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading
                    ? downloadRetryAttempt > 0
                      ? `Retrying… (${downloadRetryAttempt}/${DOWNLOAD_MAX_RETRIES})`
                      : "Downloading…"
                    : "Download file"}
                </Button>
              )}

              {/* Show Cancel button whenever the export is not yet Completed */}
              {batchExport?.state !== "Completed" && onCancelExport && (
                <Button
                  color={isStale ? "warning" : "transparent"}
                  size="sm"
                  onClick={handleCancelExport}
                >
                  {isStale ? "Stuck? Cancel & retry" : "Cancel export"}
                </Button>
              )}
            </Box>
          </div>
        </GridItem>
      )}

      <GridItem xs={12} sm={12} md={12}>
        <CardWrapper>
          <OvsTable
            columns={itemProcessData().header as Column[]}
            data={transformedData}
            checkedIds={[]}
            tableTitle="Batch Progress"
            hideRightToolbar
          />
        </CardWrapper>
      </GridItem>
    </GridContainer>
  );
};

export default BatchProgressPage;
